import {useEffect} from "react";
import {FormattedPageTitle} from "@/components/pages";
import {FormActionButtons, FormattedFormRow, FormContainer} from "@dvrt/graphql-widgets";
import {formCheckboxMargin, formPagesWidth} from "@/constants";
import {NumberField, EnumField} from "@dvrt/graphql-widgets";
import UpdateGatewayForm from "./UpdateGatewayForm";
import {useBoolState} from "@/hooks";
import {TextFieldInput} from "@/components/gateway/update/widgets";
import {Checkbox} from "@mui/material";
import OutgoingCallBarringWidget from "@/outgoing-call-barring/OutgoingCallBarringWidget";
import {CallbackType, NonPaymentBlockingType} from "@/api";
import {ContextFilters, useAuth} from "@/context/AuthProvider";
import Loading from "@/components/breadcrumbs/Loading";
import {useDomainNameQuery} from "@/queries.generated";
import {defineMessage} from "react-intl";
import {CALLBACK_TYPE_MESSAGES, NON_PAYMENT_BLOCKING_TYPE_MESSAGES} from "@/utils/translations";
import TargetSelector from "@/components/TargetSelector";
import {useDataSource, DataSource} from "./DataSource";

const restrictedCallbackTypes = [CallbackType.PreviousOptionsWithDisa, CallbackType.MatchAccordingOrFallBack];

const Content = ({id, dataSource, domainName}: {id: number; dataSource: DataSource; domainName: string}) => {
    const form = new UpdateGatewayForm(id, dataSource);
    const [enabled, enable, disable] = useBoolState(true);
    const auth = useAuth();

    async function submit() {
        disable();
        if (await form.submit()) {
            form.newInitialData(await dataSource.refetch());
        }
        enable();
    }

    const formDisabled = !enabled || !auth.hasPermission("edit_superuser_gateway", ContextFilters.domain(domainName));

    const isCallbackTypeOptionDisabled = (option: CallbackType) => {
        return form.values.multipleCallsNumber > 1 && restrictedCallbackTypes.includes(option);
    };

    useEffect(() => {
        if (form.values.multipleCallsNumber > 1 && restrictedCallbackTypes.includes(form.values.callbackType))
            form.updateValues({callbackType: CallbackType.AlwaysCallbackNumber});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.values.multipleCallsNumber]);

    return (
        <div style={{marginBottom: 20}}>
            <FormattedPageTitle
                desc={defineMessage({id: "settings", defaultMessage: "Settings"})}
                values={{name: form.values.displayName}}
            />
            <FormContainer style={{maxWidth: formPagesWidth}}>
                <FormattedFormRow label={defineMessage({id: "displayName", defaultMessage: "Name"})}>
                    <TextFieldInput field={"displayName"} form={form} disabled={formDisabled} />
                </FormattedFormRow>
                <FormattedFormRow label={defineMessage({id: "sip_username", defaultMessage: "SIP username"})}>
                    <TextFieldInput field={"sipUser"} form={form} disabled={formDisabled} />
                </FormattedFormRow>
                <FormattedFormRow label={defineMessage({id: "sip_password", defaultMessage: "SIP password"})}>
                    <TextFieldInput field={"sipPassword"} form={form} disabled={formDisabled} />
                </FormattedFormRow>
                <FormattedFormRow label={defineMessage({id: "enabled", defaultMessage: "Enabled"})}>
                    <Checkbox
                        disabled={formDisabled}
                        id={"enabled"}
                        checked={form.values.enabled}
                        onChange={(event) => form.updateValues({enabled: event.target.checked})}
                        color="primary"
                        style={{marginLeft: formCheckboxMargin}}
                    />
                </FormattedFormRow>
                <FormattedFormRow label={defineMessage({id: "callbackNumber", defaultMessage: "Callback number"})}>
                    <TargetSelector
                        disabled={formDisabled}
                        handleChange={(event) => form.updateValues({callbackNumberId: Number(event)})}
                        variant="outlined"
                        selected={form.values.callbackNumberId}
                        errors={form.errorHelper.fieldErrors("data.callbackNumberId")}
                        targets={dataSource.callbackTargets}
                        required
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "outgoing_call_barring", defaultMessage: "Outgoing call barring"})}
                    labelAlign="top"
                >
                    <OutgoingCallBarringWidget
                        masterBarrings={{
                            operator: dataSource.domainData.outgoingCallBarringOperator,
                            admin: dataSource.domainData.outgoingCallBarringAdmin,
                        }}
                        operatorBarrings={form.values.outgoingCallBarringOperator}
                        updateOperatorBarrings={(barrings) =>
                            form.updateValues({outgoingCallBarringOperator: barrings})
                        }
                        disabledOperator={formDisabled}
                    />
                </FormattedFormRow>
                <FormattedFormRow label={defineMessage({id: "CallbackType.label", defaultMessage: "Callback target"})}>
                    <EnumField
                        disabled={formDisabled}
                        value={form.values.callbackType}
                        enum_={{CallbackType}}
                        disabledItemFilter={isCallbackTypeOptionDisabled}
                        onChange={(val) =>
                            form.updateValues({
                                callbackType: val as CallbackType,
                            })
                        }
                        translations={CALLBACK_TYPE_MESSAGES}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "multipleCallsNumber", defaultMessage: "Multiple calls number"})}
                >
                    <NumberField
                        disabled={formDisabled}
                        value={form.values.multipleCallsNumber}
                        update={(val) => form.updateValues({multipleCallsNumber: val})}
                        errors={form.errorHelper.fieldErrors("data.multipleCallsNumber")}
                        min={1}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "NonPaymentBlockingType.label", defaultMessage: "Non-payment blocking"})}
                >
                    <EnumField
                        disabled={formDisabled}
                        value={form.values.nonPaymentBlockingType}
                        enum_={{NonPaymentBlockingType}}
                        onChange={(val) =>
                            form.updateValues({
                                nonPaymentBlockingType: val as NonPaymentBlockingType,
                            })
                        }
                        translations={NON_PAYMENT_BLOCKING_TYPE_MESSAGES}
                    />
                </FormattedFormRow>
                <FormattedFormRow>
                    <div style={{margin: "1em 0"}}>
                        <FormActionButtons
                            mainAction={submit}
                            secondaryAction={() => {
                                form.reset();
                            }}
                            enabled={form.hasChanged && !formDisabled}
                        />
                    </div>
                </FormattedFormRow>
            </FormContainer>
        </div>
    );
};

const UpdateGatewayPage = ({id, domainId}: {id: number; domainId: number}) => {
    const domainNameQuery = useDomainNameQuery({
        variables: {
            id: domainId,
        },
    });
    const dataSource = useDataSource(id, domainId);

    if (dataSource.loading || domainNameQuery.loading) return <Loading />;

    const domainName = domainNameQuery.data?.domains.items[0].name;

    return <Content id={id} dataSource={dataSource} domainName={domainName} />;
};

export default UpdateGatewayPage;
